import React, { useEffect, useCallback, memo } from 'react';
import Chunks from './Chunks/Chunks';
import CmsTools from '../../components/CmsTools/CmsTools';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { updateLi } from '../../redux/actions/CourseActions';
import { updateActiveSideNav } from '../../components/SideNav/SideNav';
import { ToastContainer, toast } from 'react-toastify';
import useXapi from '../../Hooks/useXapi';
import './Block.scss';

// scroll variables
window.userScrolled = false;
let lastScrollTop = 0;
let scrollDirection;
let currentChunkLi;

// used in SideNav.js so that when a chunk is clicked, block scrolls to the chunk,
// and the side nav doesn't blip from item to item.
export const setUserScroll = value => {
  window.userScrolled = value;
};

// checks to see if the scrolling chunk meets the trigger criteria
let isCurrentChunk = (elem, lastChunk) => {
  if (elem === null) return false;
  let bounding = elem.getBoundingClientRect();
  let vh = window.innerHeight;
  let trigger;
  if (scrollDirection === 'down') {
    // if scrolling down the page
    // checks to see if a chunk's top is in the top 3rd of the viewport,
    // but below the top of the viewport
    trigger = bounding.top > 0 && bounding.top < vh / 3;

    if (elem.id.split('-')[1] === lastChunk.li) {
      trigger = bounding.bottom < vh || bounding.top < vh / 3;
    }
  } else {
    // if scrolling up the page
    // checks to see if a chunk's top is in the lower 2/3rds of the viewport,
    // but above the bottom of the viewport
    trigger = bounding.bottom < vh && bounding.bottom > vh / 3;
  }
  return trigger;
};

// gets the acroll direction
let getScrollDirection = () => {
  var st = window.pageYOffset || document.documentElement.scrollTop;
  if (st > lastScrollTop) {
    scrollDirection = 'down';
  } else {
    scrollDirection = 'up';
  }
  lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling;
};

const Block = () => {
  const { xApiEvent } = useXapi();
  const dispatch = useDispatch();
  const { chunks, blockId, courseId } = useSelector(
    state => ({
      courseId: state.CourseState.courseId,
      blockId: state.CourseState.currentStep.id,
      chunks: state.CourseState.currentStep.chunks
    }),
    shallowEqual
  );

  const listenForScroll = useCallback(() => {
    // const { chunks } = currentStep;
    // do nothing if it's the homepage
    if (blockId !== courseId) {
      // keep checking for scroll direction
      getScrollDirection();

      // if the user starts scrolling before utoscroll kicks in
      // we won't auto-scroll
      if (window.userScrolled) {
        // the autoscroll functionality checks for userScroll before attempting to scroll
        // dispatch({ type: 'USER_SCROLL', userScroll: true });
        window.userScrolled = true;
        // and if it's the user scrolling, check the chunk positions.
        chunks.forEach(chunk => {
          if (
            isCurrentChunk(
              document.getElementById(`chunk-${chunk.li}`),
              chunks[chunks.length - 1]
            ) &&
            currentChunkLi !== chunk.li
          ) {
            currentChunkLi = chunk.li;
            // uodate side nav highlight
            updateActiveSideNav(chunk);
            // update the activeChunk, and replace the URL.
            dispatch(updateLi(chunk.li));
            // do xapi stuffs
            xApiEvent('Scrolled to ' + chunk.li, window.location.href);
          }
        });
      }
    }
  }, [blockId, chunks, courseId, dispatch, xApiEvent]);

  useEffect(() => {
    window.scroll({
      top: '0px;'
    });
    window.addEventListener('scroll', listenForScroll);
    window.addEventListener('wheel', e => {
      window.userScrolled = true;
    });
    window.addEventListener('touchstart', e => {
      window.userScrolled = true;
    });
    window.addEventListener('mousedown', e => {
      window.userScrolled =
        e.clientX > document.documentElement.offsetWidth ? true : false;
    });
    currentChunkLi = window.location.pathname.split('/').pop();

    return () => {
      window.removeEventListener('scroll', listenForScroll);
      window.removeEventListener('wheel', e => {
        window.userScrolled = true;
      });
      window.addEventListener('touchstart', e => {
        window.userScrolled = true;
      });
      window.removeEventListener('mousedown', e => {
        window.userScrolled =
          e.clientX > document.documentElement.offsetWidth ? true : false;
      });
    };
  }, [blockId, listenForScroll]);

  const isRTL =
    document.getElementsByTagName('html')[0].getAttribute('dir') === 'rtl'
      ? true
      : false;

  return (
    <div className="block-container">
      <CmsTools id={blockId} type="blocks" />
      <ToastContainer
        position={toast.POSITION.TOP_CENTER}
        autoClose={6000}
        progressStyle={{ background: '#64bbe3' }}
        rtl={isRTL ? true : false}
      />
      <Chunks />
    </div>
  );
};

export default memo(Block);
